<template>
  <!-- Note List -->
  <div class="g-alarm-handler g-alarm-handler__row alarm-handler">
    <div
      class="alarm-handler__urgency"
      :class="['alarm-handler__urgency--' + computedAlarm.state]"
    >
      {{ isNaN(computedAlarm.passive) ? $t('messages.sharedTexts.status.active') : '' }} {{ $t('messages.sharedTexts.status.unhandled') + ' ' + $tc('messages.sharedTexts.alarm', 1) }}
      <span
        v-if="computedAlarm.seen"
      >
        - {{ $t('messages.sharedTexts.status.markedSeen') }}
      </span>
    </div>

    <h2>{{ $t('messages.sharedTexts.alarms.' + (computedAlarm.id + 1)) }}</h2>
    <component
      :is="computedAlarm.handler"
      :alarm="computedAlarm"
      :alarm-data="alarmData"
      :store="store"
      :socket-message="SocketMessage"
    />

    <alarm-messages
      :type="1"
      :origin="computedAlarm.id"
      :date-time-format="store.getData('settings.time.currentFormat')"
      :user-id="store.getData('user.userid')"
      :device-id="store.getData('controller.deviceData.ctrlid')"
    />
    
    <!-- Alarm Next -->
    <div class="g-alarm-handler__row g-text g-text--center alarm-handler__buttons alarm-handler__buttons--grid">
      <button
        type="button"
        class="g-btn g-btn--rounded g-btn--blue"
        :class="{'disabled': alarmIndex === null || !computedHasPreviousAlarm}"
        @click.stop="goToAlarm('prev')"
      >
        <font-awesome-icon :icon="['fas', 'chevron-left']" /> {{ $tc('messages.sharedTexts.alarm', 1) }}
      </button>
      <button
        type="submit"
        class="g-btn g-btn--rounded g-btn--yellow"
        :title="$t('messages.sharedTexts.alarms.seenButton')"
        :class="{'disabled': alarmIndex === null}"
        @click.stop="changeStatusClicked('seen')"
      >
        {{ $t('messages.sharedTexts.status.seen') }} <font-awesome-icon :icon="['far', 'eye']" />
      </button>
      <button
        type="submit"
        class="g-btn g-btn--rounded g-btn--green"
        :title="$t('messages.sharedTexts.alarms.fixedButton')"
        :class="{'disabled': alarmIndex === null}"
        @click.stop="changeStatusClicked('fixed')"
      >
        {{ $t('messages.sharedTexts.status.fixed') }} <font-awesome-icon :icon="['fas', 'check']" />
      </button>
      <button
        type="button"
        class="g-btn g-btn--rounded g-btn--blue"
        :class="{'disabled': alarmIndex === null || !computedHasNextAlarm}"
        @click.stop="goToAlarm('next')"
      >
        {{ $tc('messages.sharedTexts.alarm', 1) }} <font-awesome-icon :icon="['fas', 'chevron-right']" />
      </button>
    </div>
  </div>
</template>

<script>

import {SocketHandler, LifeCycle} from '@/assets/javascript/mixins/Mixins'
import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome'
import {secondsToObj} from '@libs/Time-lib'
import moment from 'moment'
import AlarmMessages from '@/components/elements/AlarmMessages'

import ServiceRainAlarm from '@/components/combinations/ServiceRainAlarm'
import ServiceAuxAlarm from '@/components/combinations/ServiceAUXAlarm'
import ServiceEtAlarm from '@/components/combinations/ServiceETalarm'
import ServiceShortAlarm from '@/components/combinations/ServiceShortAlarm'
import ServiceStationAlarm from '@/components/combinations/ServiceStationAlarm'
import ServiceHighFlowAlarm from '@/components/combinations/ServiceHighFlowAlarm'
import ServiceMasterPumpFailureAlarm from '@/components/combinations/ServiceMasterPumpFailureAlarm'
import ServiceUnscheduledFlowAlarm from '@/components/combinations/ServiceUnscheduledFlowAlarm'
import ServiceCycleAlarm from '@/components/combinations/ServiceCycleAlarm'
import ServiceNWWPrgAlarm from '@/components/combinations/ServiceNWWPrgAlarm'
import ServiceNWWStopAlarm from '@/components/combinations/ServiceNWWStopAlarm'
import ServiceNoMoistAlarm from '@/components/combinations/ServiceNoMoistAlarm'
import ServiceMoistAlarm from '@/components/combinations/ServiceMoistAlarm'
import ServiceMissingETAlarm from '@/components/combinations/ServiceMissingETAlarm'
import ServiceMaxStationsAlarm from '@/components/combinations/ServiceMaxStationsAlarm'

export default {
  name: 'AlarmHandler',
  components: {
    'font-awesome-icon': FontAwesomeIcon,
    'alarm-messages': AlarmMessages,
    ServiceRainAlarm,
    ServiceAuxAlarm,
    ServiceEtAlarm,
    ServiceShortAlarm,
    ServiceStationAlarm,
    ServiceHighFlowAlarm,
    ServiceUnscheduledFlowAlarm,
    ServiceMasterPumpFailureAlarm,
    ServiceCycleAlarm,
    ServiceNWWPrgAlarm,
    ServiceNWWStopAlarm,
    ServiceNoMoistAlarm,
    ServiceMoistAlarm,
    ServiceMissingETAlarm,
    ServiceMaxStationsAlarm,
  },
  mixins: [SocketHandler, LifeCycle],
  props: {
    SocketMessage: {
      type: Object,
      default: function() {
        return {}
      }
    },
    ModalData: {
      type: Object,
      default: function() {
        return {}
      }
    },
    store: {
      type: Object,
      default: function() {
        return {}
      }
    }
  },
  data() {
    return {
      alarmData: {
        list: null
      },
      alarmIndex: null,
    }
  },
  computed: {
    computedAlarmList() {
      return this.store.getData('alarms').list.filter(alarm => !alarm.fixed)
    },
    computedAlarm() {
      return this.alarmIndex !== null ? this.computedAlarmList[this.alarmIndex]: this.ModalData.data
    },
    computedHasPreviousAlarm() {
      return (this.alarmIndex - 1) >= 0
    },
    computedHasNextAlarm() {
      return (this.alarmIndex + 1) <= this.computedAlarmList.length - 1
    }
  },
  watch: {
    '$route'() {
      this.$emit('CloseModal')
    },
    SocketMessage(msg) {
      if (msg.data && msg.data.data && msg.data.component === 'AlarmHandler') {
        console.debug('AlarmHandler', JSON.stringify(msg.data, null, 2))
        const programs = this.store.getData('programs')
        const stations = [].concat(this.store.getData('stations'),
          [{
            StId: '11001',
            name: 'MV',
            description: this.$t('messages.sharedTexts.stations.MV')
          }, {
            StId: '11002',
            name: 'BOO1',
            description: this.$t('messages.sharedTexts.stations.BOO1')
          }, {
            StId: '11003',
            name: 'BOO2',
            description: this.$t('messages.sharedTexts.stations.BOO2')
          }, {
            StId: '12001',
            name: 'NOMV',
            description: this.$t('messages.sharedTexts.stations.NOMV')
          }]
        )
        
        const alarms = msg.data.data.alarms

        const list = alarms.map(item => {
          return {
            programs: item.program.ID === '-1' ? [item.program] : [programs.find(program => program.ID === item.program.ID, 10)],
            stations: item.stations.map(station => {
              const data = stations.find(st => st.StId === station.StId)
              let rtnData = null

              const time = secondsToObj(station.time)
              const hours = (time.hh !== 0) ? time.hh + this.$tc('messages.sharedTexts.time.h', 1) : ''
              const minutes = (time.mm !== 0) ? time.mm + this.$tc('messages.sharedTexts.time.m', 1) : ''
              const seconds = (time.ss !== 0) ? time.ss + this.$tc('messages.sharedTexts.time.s', 1) : ''

              if(data !== undefined) {
                rtnData = {
                  time: `${hours} ${minutes} ${seconds}`,
                  StId: data.StId,
                  name: data.name
                }
              }
              
              return rtnData
            })
          }
        })

        this.$set(this.alarmData, 'list', list)
      }
    },
    computedAlarm(alarmData) {
      // console.debug('requesting alarm data')
      this.SocketMessageSend({ category: 'alarm', alarmID: alarmData.id })
    }
  },
  created() {
    const index = this.computedAlarmList.findIndex(alarm => alarm.id === this.ModalData.data.id)
    this.alarmIndex = index !== -1 ? index : null
  },
  methods: {
    SetPageMessage(Message) {
      this.$emit('SetPageMessage', Message)
    },
    changeStatusClicked(status) {
      if(this.alarmIndex === null) {
        return
      }

      const seen = status === 'seen' ? !this.computedAlarm.seen : this.computedAlarm.seen
      const fixed = status === 'fixed' ? !this.computedAlarm.fixed : this.computedAlarm.fixed

      this.SocketMessageSend({
        Category: 'Auxillary',
        Type: 'Alarms',
        Oper: 'Upd',
        AlarmId: this.computedAlarm.id ,
        Seen: seen,
        Fixed: fixed
      })

      this.computedAlarm.seen = seen

      if(fixed) {
        let alarmList = this.store.getData('alarms').list //this.computedAlarm.fixed = fixed
        const alarmId = alarmList.findIndex(alarm => alarm.id === this.computedAlarm.id)
        alarmList.splice(alarmId, 1)

        if(this.computedAlarmList.length - 2 >= 0) {
          if(this.alarmIndex > this.computedAlarmList.length - 1) {
            this.alarmIndex = this.computedAlarmList.length - 1
          }
        } else {
          this.$emit('CloseModal')
        }
      }

      this.$set(this.store.getData('alarms').list, this.alarmIndex, this.computedAlarm)
      // Object.assign(
      //   {},
      //   this.store.getData('alarms').list.find(alarm => alarm.id = this.computedAlarm.id),
      //   this.computedalarm
      // )
    },
    goToAlarm(action) {
      if(this.alarmIndex === null) {
        return
      }
      if(action === 'prev' && this.computedHasPreviousAlarm) {
        this.alarmIndex -= 1
      } else if (action === 'next' && this.computedHasNextAlarm) {
        this.alarmIndex += 1
      }
    }
  }
}
</script>

<style lang="scss">
.alarm-handler {
  margin-top: 20px;
  textarea {
    box-sizing: border-box;
    width: 100%;
    min-height: 100px;
    resize: vertical;
  }

  &__urgency {
    box-sizing: border-box;
    margin: -$application-base-padding (-$application-base-padding * 2) 10px;
    padding: 10px;
    text-align: center;
    text-transform: uppercase;
    font-family: $font-primary-medium;
    color: $color-white;
    background: $color-grey-dark;

    &--red {
      background-color: darken($color-red-error, 20);
    }
    &--yellow {
      background-color: darken(tint($color-yellow-warn, $color-red, 80), 10);
    }
    &--green {
      background-color: $color-green-ok;
    }
  }

  &__title {
    padding-bottom: 5px;
    &--underlined {
      border-bottom: 1px solid $color-grey-darker;
    }
  }

  &__buttons {
    width: 100%;

    button {
      margin: 10px 5px;
      font-size: 14px;
    }

    &--grid {
      display: flex;
      justify-content: space-between;
      align-items: stretch;

      button {
        display: inline-block;
        width: 100%;
      }
    }
  }
}
</style>
